import React, { useEffect, useRef, useState } from 'react';
import { safeArray } from '../../../components/ErrorBoundary';
import TableEmptyRow from '../../../components/TableEmptyRow';
import { bgReportsAPI } from '../../../services/bgFinanceService';
import { formatCurrency, formatPercent, LIMIT_TYPE_LABELS, utilizationBarColor } from './bgShared';
import BgExportButtons from './BgExportButtons';
import { downloadBgTableCsv, exportBgTablePdf } from './bgExportUtils';
import { notifyError } from '../../../utils/notify';

const UtilizationBar = ({ pct, available }) => {
  const width = Math.min(100, Math.max(0, pct));
  const color = available < 0 ? '#dc2626' : utilizationBarColor(pct);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 500, color, minWidth: 42 }}>{formatPercent(pct)}</span>
    </div>
  );
};

const BankWiseUtilization = () => {
  const [rows, setRows] = useState([]);
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await bgReportsAPI.getBankWiseUtilization();
        setRows(safeArray(resp?.data?.data));
      } catch (err) {
        console.error('Load utilization report failed:', err);
        notifyError(err?.response?.data?.message || 'Unable to load bank-wise utilization.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading utilization...</p>;
  }


  const handleExportPdf = () => exportBgTablePdf({ title: 'Bank-wise Utilization', subtitle: '', element: tableRef.current });
  const handleExportCsv = () => downloadBgTableCsv({ element: tableRef.current, filename: 'bank-wise-utilization.csv' });

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Bank-wise Utilization <span className="count-badge">{rows.length}</span></h2>
        <BgExportButtons onDownloadPdf={handleExportPdf} onDownloadCsv={handleExportCsv} />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Bank</th>
              <th className="amount-cell">Sanctioned Limit</th>
              <th className="amount-cell">Eligible Limit</th>
              <th className="amount-cell">Utilized Against Limit</th>
              <th className="amount-cell">Available Limit</th>
              <th>Utilization %</th>
              <th className="amount-cell">100% Cash-backed BG Amount</th>
              <th className="amount-cell">Counted Against Limit</th>
              <th className="amount-cell">Not Counted Against Limit</th>
              <th className="amount-cell">Cash Margin Blocked</th>
              <th className="amount-cell">Total BG/Bond Exposure</th>
              <th>Active BG Count</th>
              <th>Expiring BG Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <TableEmptyRow colSpan={13} message="No utilization data found." />
            ) : (
              rows.map((row) => {
                const pct = row.utilization_percent ?? 0;
                const available = row.available_limit ?? 0;
                const limitLabel = LIMIT_TYPE_LABELS[row.limit_type] || row.limit_type;
                return (
                  <tr key={`${row.bank_id}-${row.limit_id}`}>
                    <td style={{ fontWeight: 500 }}>
                      {row.bank_name}
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{limitLabel}</div>
                    </td>
                    <td className="amount-cell">{formatCurrency(row.sanctioned_amount)}</td>
                    <td className="amount-cell">{formatCurrency(row.eligible_limit)}</td>
                    <td className="amount-cell">{formatCurrency(row.utilized_against_limit)}</td>
                    <td className="amount-cell" style={{ color: available < 0 ? '#b91c1c' : undefined, fontWeight: available < 0 ? 600 : undefined }}>
                      {formatCurrency(available)}
                    </td>
                    <td><UtilizationBar pct={pct} available={available} /></td>
                    <td className="amount-cell">{formatCurrency(row.full_cash_backed_amount)}</td>
                    <td className="amount-cell">{formatCurrency(row.full_cash_backed_counted_against_limit)}</td>
                    <td className="amount-cell">{formatCurrency(row.full_cash_backed_not_counted_against_limit)}</td>
                    <td className="amount-cell">{formatCurrency(row.cash_margin_blocked)}</td>
                    <td className="amount-cell">{formatCurrency(row.total_bg_bond_exposure)}</td>
                    <td>{row.active_bg_count ?? 0}</td>
                    <td>{row.expiring_bg_count ?? 0}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankWiseUtilization;
